/**
 * Shared contract for validating props supplied via the URL before a modal or sheet is opened
 * from it (`?modal=...&modalProps=...` / `?sheet=...&sheetProps=...`). This is the security
 * boundary that stops crafted links from opening arbitrary overlays with arbitrary props — see
 * `createModalStore` / `createSheetStore`.
 */

/** Result of validating URL-supplied props. Structurally compatible with Zod's `safeParse`. */
export type UrlPropSchemaResult = { success: true; data: unknown } | { success: false; error?: unknown };

/**
 * Validates (and narrows) props supplied via the URL before the overlay opens. Any Zod schema
 * satisfies this shape, so apps pass `z.object({...})` directly without this package taking a
 * hard dependency on zod.
 */
export interface UrlPropSchema {
  safeParse: (data: unknown) => UrlPropSchemaResult;
}

/**
 * Parses an overlay-props URL param (`modalProps` / `sheetProps`) and validates it against the
 * overlay's opt-in schema. Returns the narrowed props on success; `{ ok: false }` when the param
 * is malformed JSON or fails validation — in which case callers must not open the overlay. A
 * missing param validates as an empty object (overlays opened from the URL with no payload).
 */
export function parseUrlOverlayProps(
  rawParam: string | null | undefined,
  schema: UrlPropSchema,
): { ok: true; data: unknown } | { ok: false } {
  let raw: unknown = {};
  if (rawParam) {
    try {
      raw = JSON.parse(decodeURIComponent(rawParam));
    } catch {
      return { ok: false };
    }
  }
  const parsed = schema.safeParse(raw);
  return parsed.success ? { ok: true, data: parsed.data } : { ok: false };
}
